import { createSlice } from "@reduxjs/toolkit"
import axios from "axios"

// Customer address book. One customer has many addresses and exactly one
// default; the default is what checkout and redemption preselect.
//
// The list is kept as the single source of truth in the store, so every screen
// that shows an address picker stays in step after an add / edit / delete
// without each one re-fetching.
const initialState = {
    listLoading: false,
    listLoaded: false,
    list: [],
    saving: false,
    saveSuccess: false,
    saveFail: false,
    errorMessage: null,
}

export const addressesSlice = createSlice({
    name: "addresses",
    initialState,
    reducers: {
        listRequest: (state) => {
            state.listLoading = true
            state.errorMessage = null
        },
        listSuccess: (state, action) => {
            state.listLoading = false
            state.listLoaded = true
            state.list = action.payload || []
        },
        listFail: (state, action) => {
            state.listLoading = false
            state.errorMessage = action.payload
        },
        saveRequest: (state) => {
            state.saving = true
            state.saveSuccess = false
            state.saveFail = false
            state.errorMessage = null
        },
        saveSuccess: (state) => {
            state.saving = false
            state.saveSuccess = true
        },
        saveFail: (state, action) => {
            state.saving = false
            state.saveFail = true
            state.errorMessage = action.payload
        },
        resetSave: (state) => {
            state.saving = false
            state.saveSuccess = false
            state.saveFail = false
            state.errorMessage = null
        },
    },
})

export const { listRequest, listSuccess, listFail, saveRequest, saveSuccess, saveFail, resetSave } =
    addressesSlice.actions

const message = (error) =>
    error.response?.data?.errorMessage || error.message || "Network error occurred"

export const doListAddresses = () => async (dispatch) => {
    dispatch(listRequest())
    try {
        const response = await axios.get("/api/customer/address")
        dispatch(listSuccess(response.data.data))
    } catch (error) {
        dispatch(listFail(message(error)))
    }
}

// Each mutation re-lists afterwards rather than patching state locally: the
// server decides which row ends up default (creating the first address, or
// deleting the current default, both promote one), so the returned list is the
// only reliable picture.
const mutate = (request) => async (dispatch) => {
    dispatch(saveRequest())
    try {
        await request()
        dispatch(saveSuccess())
        dispatch(doListAddresses())
    } catch (error) {
        dispatch(saveFail(message(error)))
    }
}

export const doCreateAddress = (payload) =>
    mutate(() => axios.post("/api/customer/address", payload))

export const doUpdateAddress = (id, payload) =>
    mutate(() => axios.put(`/api/customer/address/${id}`, payload))

export const doSetDefaultAddress = (id) =>
    mutate(() => axios.put(`/api/customer/address/${id}/default`))

export const doDeleteAddress = (id) =>
    mutate(() => axios.delete(`/api/customer/address/${id}`))

export const getAddresses = (state) => state.addresses.list
export const getAddressesLoading = (state) => state.addresses.listLoading
export const getAddressesLoaded = (state) => state.addresses.listLoaded
export const getAddressSaving = (state) => state.addresses.saving
export const getAddressSaveSuccess = (state) => state.addresses.saveSuccess
export const getAddressSaveFail = (state) => state.addresses.saveFail
export const getAddressErrorMessage = (state) => state.addresses.errorMessage
export const getDefaultAddress = (state) =>
    state.addresses.list.find((a) => a.is_default === 1 || a.is_default === true) || state.addresses.list[0] || null

export default addressesSlice.reducer
